import React, { useMemo, useRef, useState } from "react";
import FlightProgress from "../layout/FlightProgress";
import StickyBookingSummary from "../details/StickyBookingSummary";
import {
  MOCK_PASSENGERS_FORM,
  passengersRequirePassport,
} from "../mock/flightEngineMockData";
import { formatDateInput } from "../../../utils/ddMmDateInputFormat";
import "./PassengersScreen.css";

function ddMmYyyyToIso(value) {
  const raw = String(value || "").trim();
  const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function isoToDdMmYyyy(value) {
  const raw = String(value || "").trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function Field({ label, optional = false, error = null, children }) {
  return (
    <label className={`fe2-pax-field${error ? " fe2-pax-field--error" : ""}`}>
      <span className="fe2-pax-field__label">
        {label}
        {optional ? (
          <span className="fe2-pax-field__optional">אופציונלי</span>
        ) : null}
      </span>
      {children}
      {error ? <span className="fe2-pax-field__error">{error}</span> : null}
    </label>
  );
}

function DateField({ label, value, error, onChange }) {
  const iso = ddMmYyyyToIso(value);
  const pickerRef = useRef(null);

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      el.showPicker();
      return;
    }
    el.click();
  };

  return (
    <Field label={label} error={error}>
      <div className="fe2-pax-date">
        <input
          className="fe2-pax-input fe2-pax-date__text fe2-ltr"
          type="text"
          dir="ltr"
          inputMode="numeric"
          autoComplete="bday"
          placeholder="DD/MM/YYYY"
          value={value || ""}
          onChange={(e) => onChange(formatDateInput(e.target.value))}
        />
        <button
          type="button"
          className="fe2-pax-date__btn"
          aria-label={`${label} — בורר תאריך`}
          onClick={openPicker}
        >
          <span className="fe2-pax-date__btn-icon" aria-hidden />
        </button>
        <input
          ref={pickerRef}
          className="fe2-pax-date__native"
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          value={iso}
          onChange={(e) => onChange(isoToDdMmYyyy(e.target.value))}
        />
      </div>
    </Field>
  );
}

function statusTone(status) {
  if (status === "complete") return "complete";
  if (status === "incomplete") return "incomplete";
  return "untouched";
}

const REQUIRED_PERSONAL_KEYS = [
  "firstName",
  "lastName",
  "gender",
  "dateOfBirth",
  "nationality",
];

const REQUIRED_PASSPORT_KEYS = [
  "passportNumber",
  "issuingCountry",
  "passportExpiry",
];

const FIELD_ERROR_LABELS = {
  firstName: "שדה חובה",
  lastName: "שדה חובה",
  gender: "שדה חובה",
  dateOfBirth: "שדה חובה",
  nationality: "שדה חובה",
  passportNumber: "שדה חובה",
  issuingCountry: "שדה חובה",
  passportExpiry: "שדה חובה",
};

function requiredKeysForPassenger(requirePassport, passportRequiredKeys = null) {
  if (!requirePassport) return REQUIRED_PERSONAL_KEYS;
  const passport =
    Array.isArray(passportRequiredKeys) && passportRequiredKeys.length
      ? passportRequiredKeys
      : REQUIRED_PASSPORT_KEYS;
  return [...REQUIRED_PERSONAL_KEYS, ...passport];
}

function buildFieldErrors(
  values,
  requirePassport,
  specialErrors = {},
  passportRequiredKeys = null
) {
  const errors = {};
  const required = requiredKeysForPassenger(requirePassport, passportRequiredKeys);
  for (const key of required) {
    if (!String(values?.[key] ?? "").trim()) {
      errors[key] = FIELD_ERROR_LABELS[key] || "שדה חובה";
    }
  }
  const relevant = new Set(required);
  // Keep specialty mock messages only for visible/required fields that still have a value.
  for (const [key, message] of Object.entries(specialErrors || {})) {
    if (!relevant.has(key)) continue;
    if (String(values?.[key] ?? "").trim() && !errors[key]) {
      errors[key] = message;
    }
  }
  return errors;
}

function derivePassengerStatus(
  values,
  requirePassport,
  specialErrors = {},
  passportRequiredKeys = null
) {
  const keys = requiredKeysForPassenger(requirePassport, passportRequiredKeys);
  const filled = keys.filter((k) => String(values?.[k] ?? "").trim()).length;
  const errors = buildFieldErrors(
    values,
    requirePassport,
    specialErrors,
    passportRequiredKeys
  );
  if (Object.keys(errors).length === 0) return "complete";
  if (filled === 0) return "untouched";
  return "incomplete";
}

function enrichPassenger(passenger, requirePassport, passportRequiredKeys = null) {
  const specialErrors = passenger.specialErrors || {};
  const errors = buildFieldErrors(
    passenger.values,
    requirePassport,
    specialErrors,
    passportRequiredKeys
  );
  const status = derivePassengerStatus(
    passenger.values,
    requirePassport,
    specialErrors,
    passportRequiredKeys
  );
  return {
    ...passenger,
    errors,
    status,
    collapsed: status === "complete" ? Boolean(passenger.collapsed) : false,
  };
}

function PassengerCard({
  passenger,
  fields,
  genderOptions,
  nationalityOptions,
  statusLabels,
  namesHint,
  requirePassport,
  passportDeferredNote,
  onChange,
  onToggleCollapse,
}) {
  const v = passenger.values;
  const errors = passenger.errors || {};
  const tone = statusTone(passenger.status);
  const statusLabel = statusLabels[passenger.status] || statusLabels.untouched;
  const missingCount = Object.keys(errors).length;
  const missingCountLabel =
    missingCount === 1
      ? "1 שדה חסר"
      : missingCount > 1
        ? `${missingCount} שדות חסרים`
        : null;
  const nationalityLabel =
    nationalityOptions.find((o) => o.value === v.nationality)?.label ||
    v.nationality ||
    "";

  if (passenger.collapsed && passenger.status === "complete") {
    return (
      <section className="fe2-pax-card fe2-pax-card--collapsed">
        <div className="fe2-pax-card__collapsed-main">
          <span className="fe2-pax-card__check" aria-hidden>
            ✓
          </span>
          <div className="fe2-pax-card__collapsed-text">
            <strong className="fe2-pax-card__collapsed-name fe2-ltr">
              {v.firstName}
              {v.middleName ? ` ${v.middleName}` : ""} {v.lastName}
            </strong>
            <span className="fe2-pax-card__collapsed-meta">
              {passenger.typeLabel}
              {v.dateOfBirth ? ` · ${v.dateOfBirth}` : ""}
              {nationalityLabel ? ` · ${nationalityLabel}` : ""}
            </span>
          </div>
        </div>
        <button
          type="button"
          className="fe2-pax-card__edit"
          onClick={() => onToggleCollapse(passenger.id, false)}
        >
          עריכה
        </button>
      </section>
    );
  }

  return (
    <section
      className={`fe2-pax-card fe2-pax-card--${tone}`}
      aria-labelledby={`${passenger.id}-title`}
    >
      <header className="fe2-pax-card__head">
        <div className="fe2-pax-card__head-main">
          <h2 id={`${passenger.id}-title`} className="fe2-pax-card__title">
            {passenger.indexLabel}
          </h2>
          <span className="fe2-pax-card__type">{passenger.typeLabel}</span>
        </div>
        <div className="fe2-pax-card__status-wrap">
          <span className={`fe2-pax-status fe2-pax-status--${tone}`}>
            {statusLabel}
          </span>
          {tone === "incomplete" && missingCountLabel ? (
            <span className="fe2-pax-missing-count">{missingCountLabel}</span>
          ) : null}
        </div>
      </header>

      <div className="fe2-pax-block">
        <h3 className="fe2-pax-block__title">פרטים אישיים</h3>
        <p className="fe2-pax-block__hint">{namesHint}</p>

        <div className="fe2-pax-grid fe2-pax-grid--names">
          <Field label={fields.firstName.label} error={errors.firstName}>
            <input
              className="fe2-pax-input fe2-ltr"
              type="text"
              dir="ltr"
              autoComplete="given-name"
              placeholder={fields.firstName.placeholder}
              value={v.firstName}
              onChange={(e) =>
                onChange(passenger.id, "firstName", e.target.value)
              }
            />
          </Field>
          <Field
            label={fields.middleName.label}
            optional
            error={errors.middleName}
          >
            <input
              className="fe2-pax-input fe2-ltr"
              type="text"
              dir="ltr"
              autoComplete="additional-name"
              placeholder={fields.middleName.placeholder}
              value={v.middleName}
              onChange={(e) =>
                onChange(passenger.id, "middleName", e.target.value)
              }
            />
          </Field>
          <Field label={fields.lastName.label} error={errors.lastName}>
            <input
              className="fe2-pax-input fe2-ltr"
              type="text"
              dir="ltr"
              autoComplete="family-name"
              placeholder={fields.lastName.placeholder}
              value={v.lastName}
              onChange={(e) =>
                onChange(passenger.id, "lastName", e.target.value)
              }
            />
          </Field>
        </div>

        <div className="fe2-pax-grid">
          <Field label={fields.gender.label} error={errors.gender}>
            <select
              className="fe2-pax-input"
              value={v.gender}
              onChange={(e) => onChange(passenger.id, "gender", e.target.value)}
            >
              {genderOptions.map((opt) => (
                <option key={opt.value || "empty"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
          <DateField
            label={fields.dateOfBirth.label}
            value={v.dateOfBirth}
            error={errors.dateOfBirth}
            onChange={(next) => onChange(passenger.id, "dateOfBirth", next)}
          />
          <Field label={fields.nationality.label} error={errors.nationality}>
            <select
              className="fe2-pax-input"
              value={v.nationality}
              onChange={(e) =>
                onChange(passenger.id, "nationality", e.target.value)
              }
            >
              {nationalityOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      <div className="fe2-pax-block">
        <h3 className="fe2-pax-block__title">פרטי מסמך נסיעה</h3>
        {requirePassport ? (
          <div className="fe2-pax-grid fe2-pax-grid--docs">
            <Field
              label={fields.passportNumber.label}
              error={errors.passportNumber}
            >
              <input
                className="fe2-pax-input fe2-ltr"
                type="text"
                dir="ltr"
                placeholder={fields.passportNumber.placeholder}
                value={v.passportNumber}
                onChange={(e) =>
                  onChange(passenger.id, "passportNumber", e.target.value)
                }
              />
            </Field>
            <Field
              label={fields.issuingCountry.label}
              error={errors.issuingCountry}
            >
              <select
                className="fe2-pax-input"
                value={v.issuingCountry}
                onChange={(e) =>
                  onChange(passenger.id, "issuingCountry", e.target.value)
                }
              >
                {nationalityOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Field>
            <DateField
              label={fields.passportIssueDate.label}
              value={v.passportIssueDate}
              error={errors.passportIssueDate}
              onChange={(next) =>
                onChange(passenger.id, "passportIssueDate", next)
              }
            />
            <DateField
              label={fields.passportExpiry.label}
              value={v.passportExpiry}
              error={errors.passportExpiry}
              onChange={(next) =>
                onChange(passenger.id, "passportExpiry", next)
              }
            />
          </div>
        ) : (
          <div className="fe2-pax-docs-collapsed">
            <span className="fe2-pax-docs-collapsed__text">
              {passportDeferredNote}
            </span>
          </div>
        )}
      </div>

      {passenger.status === "complete" ? (
        <div className="fe2-pax-card__foot">
          <button
            type="button"
            className="fe2-pax-card__collapse"
            onClick={() => onToggleCollapse(passenger.id, true)}
          >
            כווץ פרטים
          </button>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Passengers Design Preview — mock ViewModel only. No booking integration.
 */
export default function PassengersScreen({
  offer,
  search,
  formModel = MOCK_PASSENGERS_FORM,
  onContinue,
  onBack,
  passengers: passengersProp = null,
  contact: contactProp = null,
  onPassengerFieldChange = null,
  onContactFieldChange = null,
  continueDisabled: continueDisabledProp = null,
  requirePassport: requirePassportProp = null,
  passportRequiredKeys = null,
}) {
  const requirePassport =
    requirePassportProp != null
      ? Boolean(requirePassportProp)
      : passengersRequirePassport(offer);
  const controlled = Array.isArray(passengersProp);
  const [collapsedById, setCollapsedById] = useState({});
  const [localPassengers, setLocalPassengers] = useState(() =>
    formModel.passengers.map((p) =>
      enrichPassenger(
        {
          ...p,
          values: { ...p.values },
          specialErrors: { ...(p.errors || {}) },
          collapsed: p.collapsed,
        },
        requirePassport,
        passportRequiredKeys
      )
    )
  );
  const [localContact, setLocalContact] = useState(() => ({
    ...formModel.contact.values,
  }));

  const passengers = controlled
    ? passengersProp.map((p) =>
        enrichPassenger(
          {
            ...p,
            values: { ...(p.values || {}) },
            specialErrors: { ...(p.specialErrors || p.errors || {}) },
            collapsed:
              collapsedById[p.id] != null ? collapsedById[p.id] : p.collapsed,
          },
          requirePassport,
          passportRequiredKeys
        )
      )
    : localPassengers;
  const contact = contactProp || localContact;

  const derivedContinueDisabled = useMemo(
    () => passengers.some((p) => p.status !== "complete"),
    [passengers]
  );
  const continueDisabled =
    continueDisabledProp != null ? continueDisabledProp : derivedContinueDisabled;

  const handleChange = (id, key, value) => {
    if (controlled) {
      if (typeof onPassengerFieldChange === "function") {
        onPassengerFieldChange(id, key, value);
      }
      return;
    }
    setLocalPassengers((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        const nextSpecial = { ...(p.specialErrors || {}) };
        // Clear specialty mock error once the user edits that field.
        if (nextSpecial[key]) delete nextSpecial[key];
        return enrichPassenger(
          {
            ...p,
            values: { ...p.values, [key]: value },
            specialErrors: nextSpecial,
            collapsed: false,
          },
          requirePassport,
          passportRequiredKeys
        );
      })
    );
  };

  const handleToggleCollapse = (id, collapsed) => {
    if (controlled) {
      setCollapsedById((prev) => ({ ...prev, [id]: collapsed }));
      return;
    }
    setLocalPassengers((prev) =>
      prev.map((p) => (p.id === id ? { ...p, collapsed } : p))
    );
  };

  const subtitle = search
    ? `${search.originCity} → ${search.destinationCity} · ${search.tripTypeLabel} · ${search.passengersLabel}`
    : "";

  return (
    <div className="fe2-pax-page">
      <header className="fe2-pax-head">
        <div className="fe2-pax-head__text">
          <h1 className="fe2-pax-head__title">{formModel.introTitle}</h1>
          {subtitle ? <p className="fe2-pax-head__subtitle">{subtitle}</p> : null}
        </div>
        <button type="button" className="fe2-pax-head__back" onClick={onBack}>
          ← חזרה לפרטי הטיסה
        </button>
      </header>

      <FlightProgress activeStep="passengers" />

      <div className="fe2-pax-page__grid">
        <div className="fe2-pax-page__main">
          {continueDisabled ? (
            <p className="fe2-pax-blocker" role="status">
              לא ניתן להמשיך — יש להשלים את השדות המסומנים באדום אצל הנוסעים עם
              סטטוס ״חסר מידע״.
            </p>
          ) : null}

          {passengers.map((passenger) => (
            <PassengerCard
              key={passenger.id}
              passenger={passenger}
              fields={formModel.fields}
              genderOptions={formModel.genderOptions}
              nationalityOptions={formModel.nationalityOptions}
              statusLabels={formModel.statusLabels}
              namesHint={formModel.namesHint}
              requirePassport={requirePassport}
              passportDeferredNote={formModel.passportDeferredNote}
              onChange={handleChange}
              onToggleCollapse={handleToggleCollapse}
            />
          ))}

          <section className="fe2-pax-card fe2-pax-card--contact">
            <header className="fe2-pax-card__head">
              <h2 className="fe2-pax-card__title">{formModel.contact.title}</h2>
            </header>
            <p className="fe2-pax-block__hint">{formModel.contact.note}</p>
            <div className="fe2-pax-grid">
              <Field label={formModel.contact.name.label}>
                <input
                  className="fe2-pax-input fe2-ltr"
                  type="text"
                  dir="ltr"
                  placeholder={formModel.contact.name.placeholder}
                  value={contact.name || ""}
                  onChange={(e) => {
                    if (controlled && typeof onContactFieldChange === "function") {
                      onContactFieldChange("name", e.target.value);
                      return;
                    }
                    setLocalContact((c) => ({ ...c, name: e.target.value }));
                  }}
                />
              </Field>
              <Field label={formModel.contact.phone.label}>
                <input
                  className="fe2-pax-input fe2-ltr"
                  type="tel"
                  dir="ltr"
                  autoComplete="tel"
                  placeholder={formModel.contact.phone.placeholder}
                  value={contact.phone || ""}
                  onChange={(e) => {
                    if (controlled && typeof onContactFieldChange === "function") {
                      onContactFieldChange("phone", e.target.value);
                      return;
                    }
                    setLocalContact((c) => ({ ...c, phone: e.target.value }));
                  }}
                />
              </Field>
              <Field label={formModel.contact.email.label}>
                <input
                  className="fe2-pax-input fe2-ltr"
                  type="email"
                  dir="ltr"
                  autoComplete="email"
                  placeholder={formModel.contact.email.placeholder}
                  value={contact.email || ""}
                  onChange={(e) => {
                    if (controlled && typeof onContactFieldChange === "function") {
                      onContactFieldChange("email", e.target.value);
                      return;
                    }
                    setLocalContact((c) => ({ ...c, email: e.target.value }));
                  }}
                />
              </Field>
            </div>
          </section>
        </div>

        <div className="fe2-pax-page__side">
          <StickyBookingSummary
            offer={offer}
            search={search}
            onContinue={onContinue}
            onBack={onBack}
            continueLabel="המשך לתוספות"
            backLabel="חזרה לפרטי הטיסה"
            continueDisabled={continueDisabled}
          />
        </div>
      </div>
    </div>
  );
}
